import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CreatableCombobox,
  DataList,
  Dialog,
  PageTitle,
  SelectField,
  TextField,
  TEXT_LINK,
  useToast,
} from '@rooming/ui';
import { HOTEL_TIME_ZONES, timeZoneForCountry } from '@rooming/web-core';
import { useState, type SyntheticEvent } from 'react';
import { Link } from 'react-router';
import { backoffice, type Group, type Hotel } from '../api';
import { Loaded, messageOf, useCommand } from '../common';
import { useSession } from '../session';

// ── Groups ──

export function GroupsPage() {
  const { hasRole } = useSession();
  const canWrite = hasRole('TENANT_ADMIN');
  const groups = useQuery({ queryKey: ['groups'], queryFn: backoffice.groups });
  const contracts = useQuery({ queryKey: ['contracts'], queryFn: backoffice.contracts });
  const schools = useQuery({ queryKey: ['schools'], queryFn: backoffice.schools });
  const [search, setSearch] = useState('');
  const { busy, run } = useCommand();
  const empty = { schoolId: '', contractNumber: '', code: '', name: '' };
  const [form, setForm] = useState(empty);
  const queryClient = useQueryClient();
  const toast = useToast();

  const schoolOf = (contractId: string): string => {
    const contract = contracts.data?.find((c) => c.id === contractId);
    return schools.data?.find((s) => s.id === contract?.schoolId)?.name ?? '—';
  };
  const schoolOptions = (schools.data ?? [])
    .filter((school) => school.status === 'ACTIVE')
    .map((school) => ({ value: school.id, label: school.name }));

  /** Typed in the combobox and not found: created on the spot and selected (prompt21). */
  async function createSchool(name: string): Promise<string | undefined> {
    try {
      const school = await backoffice.createSchool({ name });
      await queryClient.invalidateQueries({ queryKey: ['schools'] });
      toast('success', `Colegio «${school.name}» creado.`);
      return school.id;
    } catch (error) {
      toast('error', messageOf(error));
      return undefined;
    }
  }

  async function create(event: SyntheticEvent): Promise<void> {
    event.preventDefault();
    const ok = await run(
      () => backoffice.createGroup({ ...form, contractNumber: form.contractNumber.trim() }),
      'Grupo creado.',
      [['groups'], ['contracts']],
    );
    if (ok) setForm(empty);
  }

  return (
    <>
      <PageTitle subtitle="Cada grupo es un curso que viaja. Todo el trabajo diario ocurre dentro de su ficha.">
        Grupos
      </PageTitle>
      <div className="flex flex-col gap-5">
        <TextField
          label="Buscar por código, nombre o colegio"
          type="search"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />
        <Loaded query={groups}>
          {(items) => {
            const needle = search.trim().toLowerCase();
            const rows = items.filter(
              (g) => needle === '' || `${g.code} ${g.name} ${schoolOf(g.contractId)}`.toLowerCase().includes(needle),
            );
            return (
              <DataList<Group>
                caption="Grupos"
                rows={rows}
                rowKey={(g) => g.id}
                empty={items.length === 0 ? 'Todavía no hay grupos.' : 'Ningún grupo coincide con la búsqueda.'}
                columns={[
                  { header: 'Código', cell: (g) => <span className="font-mono">{g.code}</span> },
                  { header: 'Grupo', cell: (g) => g.name },
                  // prompt20 §1: the school is a secondary, read-only reference (ERP) in the administrator's tables.
                  {
                    // One contract is one group (owner, 2026-10-07; migration 0019).
                    header: 'Contrato',
                    cell: (g) => (
                      <span className="font-mono text-sm text-muted">
                        {contracts.data?.find((c) => c.id === g.contractId)?.contractNumber ?? '—'}
                      </span>
                    ),
                  },
                  {
                    header: 'Colegio',
                    cell: (g) => <span className="text-sm text-muted">{schoolOf(g.contractId)}</span>,
                  },
                  {
                    header: 'Ficha',
                    end: true,
                    cell: (g) => (
                      <Link to={`/grupos/${g.id}`} className={TEXT_LINK} aria-label={`Abrir la ficha de ${g.name}`}>
                        Abrir
                      </Link>
                    ),
                  },
                ]}
              />
            );
          }}
        </Loaded>
        {canWrite && (
          <Card>
            <h2 className="mb-3 text-lg font-semibold">Nuevo grupo</h2>
            <form className="grid gap-4 md:grid-cols-2" onSubmit={(event) => void create(event)}>
              <CreatableCombobox
                label="Colegio"
                hint="Buscalo escribiendo. Si no está, escribí el nombre completo y apretá Enter para crearlo."
                options={schoolOptions}
                value={form.schoolId}
                onChange={(schoolId) => {
                  setForm((current) => ({ ...current, schoolId }));
                }}
                onCreate={createSchool}
              />
              <TextField
                label="Código de contrato"
                hint="El número del contrato en el ERP. Cada contrato es de un solo grupo: si ya lo usa otro grupo, el sistema te lo dice."
                required
                maxLength={40}
                value={form.contractNumber}
                onChange={(event) => {
                  setForm({ ...form, contractNumber: event.target.value });
                }}
              />
              <TextField
                label="Código del grupo"
                required
                maxLength={40}
                value={form.code}
                onChange={(event) => {
                  setForm({ ...form, code: event.target.value });
                }}
              />
              <TextField
                label="Nombre"
                required
                maxLength={120}
                value={form.name}
                onChange={(event) => {
                  setForm({ ...form, name: event.target.value });
                }}
              />
              <div className="md:col-span-2">
                <Button
                  type="submit"
                  loading={busy}
                  disabled={
                    form.schoolId === '' ||
                    form.contractNumber.trim() === '' ||
                    form.code.trim() === '' ||
                    form.name.trim() === ''
                  }
                >
                  Crear grupo
                </Button>
              </div>
            </form>
          </Card>
        )}
      </div>
    </>
  );
}

// ── Hotels ──

const EMAILS_HINT =
  'Separados por coma (hasta 10). Reciben el resumen cuando el firmante de un grupo de este hotel confirma la distribución.';

/** "a@x.com, b@y.com" ⇒ the list the API expects (the server validates each address). */
const emailList = (text: string): string[] =>
  text
    .split(/[,;\s]+/)
    .map((value) => value.trim())
    .filter((value) => value !== '');

const OTHER_ZONE = '__otra';

export function HotelsPage() {
  const { hasRole } = useSession();
  const hotels = useQuery({ queryKey: ['hotels'], queryFn: backoffice.hotels });
  const { busy, run } = useCommand();
  const empty = { name: '', city: '', countryCode: 'CL', timezone: 'America/Santiago', emails: '' };
  const [form, setForm] = useState(empty);
  // The time zone is chosen from the destinations Funtour works in; «Otra» lets an operator type any other IANA name.
  const [otherZone, setOtherZone] = useState(false);
  // The hotel whose addresses are being edited, and the text of the field.
  const [editing, setEditing] = useState<Hotel>();
  const [emails, setEmails] = useState('');

  return (
    <>
      <PageTitle subtitle="Las fechas de cada estadía se muestran en la hora del hotel.">Hoteles</PageTitle>
      <div className="flex flex-col gap-5">
        <Loaded query={hotels}>
          {(items) => (
            <DataList<Hotel>
              caption="Hoteles"
              rows={items}
              rowKey={(h) => h.id}
              empty="Todavía no hay hoteles."
              columns={[
                { header: 'Hotel', cell: (h) => h.name },
                { header: 'Ciudad', cell: (h) => `${h.city} (${h.countryCode})` },
                { header: 'Zona horaria', cell: (h) => <span className="font-mono text-sm">{h.timezone}</span> },
                {
                  header: 'Correos del hotel',
                  cell: (h) => (
                    <span className="flex flex-col items-start gap-1">
                      <span className="text-sm break-all">
                        {h.contactEmails.length === 0 ? 'Sin correos' : h.contactEmails.join(', ')}
                      </span>
                      {hasRole('TENANT_ADMIN') && (
                        <Button
                          variant="ghost"
                          aria-label={`Editar los correos de ${h.name}`}
                          onClick={() => {
                            setEditing(h);
                            setEmails(h.contactEmails.join(', '));
                          }}
                        >
                          Editar correos
                        </Button>
                      )}
                    </span>
                  ),
                },
              ]}
            />
          )}
        </Loaded>
        {hasRole('TENANT_ADMIN') && (
          <Card>
            <h2 className="mb-3 text-lg font-semibold">Nuevo hotel</h2>
            <form
              className="grid gap-4 md:grid-cols-2"
              onSubmit={(event) => {
                event.preventDefault();
                void run(
                  () =>
                    backoffice.createHotel({
                      name: form.name,
                      city: form.city,
                      countryCode: form.countryCode,
                      timezone: form.timezone,
                      contactEmails: emailList(form.emails),
                    }),
                  'Hotel creado.',
                  [['hotels']],
                ).then((ok) => {
                  if (ok) {
                    setForm(empty);
                    setOtherZone(false);
                  }
                });
              }}
            >
              <TextField
                label="Nombre"
                required
                value={form.name}
                onChange={(event) => {
                  setForm({ ...form, name: event.target.value });
                }}
              />
              <TextField
                label="Ciudad"
                required
                value={form.city}
                onChange={(event) => {
                  setForm({ ...form, city: event.target.value });
                }}
              />
              <TextField
                label="País (código de 2 letras)"
                required
                maxLength={2}
                value={form.countryCode}
                onChange={(event) => {
                  const countryCode = event.target.value.toUpperCase();
                  // Typing the country suggests its usual zone (unless the operator is typing another one by hand).
                  const suggested = otherZone ? undefined : timeZoneForCountry(countryCode);
                  setForm({ ...form, countryCode, ...(suggested === undefined ? {} : { timezone: suggested }) });
                }}
              />
              <SelectField
                label="Zona horaria del hotel"
                hint="La del DESTINO, no la del colegio: un hotel en Camboriú es de Brasil (America/Sao_Paulo) aunque el grupo sea de Chile. Define la hora en que se ven las estadías."
                required
                options={[
                  ...HOTEL_TIME_ZONES.map((zone) => ({ value: zone.value, label: zone.label })),
                  { value: OTHER_ZONE, label: 'Otra… (escribir el nombre técnico)' },
                ]}
                value={otherZone ? OTHER_ZONE : form.timezone}
                onChange={(event) => {
                  if (event.target.value === OTHER_ZONE) {
                    setOtherZone(true);
                    setForm({ ...form, timezone: '' });
                  } else {
                    setOtherZone(false);
                    setForm({ ...form, timezone: event.target.value });
                  }
                }}
              />
              {otherZone && (
                <TextField
                  label="Nombre técnico de la zona (IANA)"
                  hint="Con el formato Continente/Ciudad, por ejemplo America/Bogota o America/Lima. No es el nombre de la ciudad del hotel."
                  required
                  value={form.timezone}
                  onChange={(event) => {
                    setForm({ ...form, timezone: event.target.value });
                  }}
                />
              )}
              <TextField
                className="md:col-span-2"
                label="Correos del hotel (opcional)"
                hint={EMAILS_HINT}
                value={form.emails}
                onChange={(event) => {
                  setForm({ ...form, emails: event.target.value });
                }}
              />
              <div className="md:col-span-2">
                <Button
                  type="submit"
                  loading={busy}
                  disabled={
                    form.name.trim() === '' ||
                    form.city.trim() === '' ||
                    form.countryCode.length !== 2 ||
                    form.timezone.trim() === ''
                  }
                >
                  Crear hotel
                </Button>
              </div>
            </form>
          </Card>
        )}
      </div>
      <Dialog
        open={editing !== undefined}
        title={`Correos de ${editing?.name ?? 'el hotel'}`}
        onClose={() => {
          setEditing(undefined);
        }}
        actions={
          <>
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => {
                setEditing(undefined);
              }}
            >
              Cancelar
            </Button>
            <Button
              loading={busy}
              onClick={() => {
                if (editing === undefined) return;
                void run(() => backoffice.setHotelEmails(editing.id, emailList(emails)), 'Correos guardados.', [
                  ['hotels'],
                ]).then((ok) => {
                  if (ok) setEditing(undefined);
                });
              }}
            >
              Guardar
            </Button>
          </>
        }
      >
        <TextField
          label="Correos del hotel"
          hint={EMAILS_HINT}
          value={emails}
          onChange={(event) => {
            setEmails(event.target.value);
          }}
        />
      </Dialog>
    </>
  );
}
